import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Target, ArrowLeft, Loader2 } from 'lucide-react';

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await login(formData.email, formData.password);
            toast.success('Welcome back, player!');
            navigate('/dashboard');
        } catch (error) {
            const message = error.response?.data?.detail || 'Login failed. Check your credentials.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6 hero-gradient">
            <div className="w-full max-w-md">
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to home
                </Link>
                
                <div className="glass rounded-xl p-8 animate-fadeIn">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-2 bg-primary/20 rounded-lg">
                            <Target className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Welcome Back</h1>
                            <p className="text-sm text-muted-foreground">Login to continue your journey</p>
                        </div>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                data-testid="login-email-input"
                                id="email"
                                type="email"
                                placeholder="player@example.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                className="bg-black/50 border-white/10 focus:border-primary"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                data-testid="login-password-input"
                                id="password"
                                type="password"
                                placeholder="Enter your password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                required
                                className="bg-black/50 border-white/10 focus:border-primary"
                            />
                        </div>
                        
                        <Button 
                            data-testid="login-submit-btn"
                            type="submit" 
                            disabled={loading}
                            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider py-6 btn-glow"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Logging in...
                                </>
                            ) : (
                                'Login'
                            )}
                        </Button>
                    </form>
                    
                    <p className="text-center text-sm text-muted-foreground mt-6">
                        New player?{' '}
                        <Link to="/register" className="text-primary hover:underline font-medium">
                            Create account
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
